import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBagIcon,
  CurrencyEuroIcon,
  CubeIcon,
  UsersIcon,
  ClockIcon,
  ArrowPathIcon,
  SignalIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useUIStore } from '@/stores/uiStore';
import { useDashboard } from '@/hooks/useDashboard';
import { useRevenueReport, useAnalyticsSettings } from '@/hooks/useAnalytics';
import { computeDateRange } from '@/utils/dateRange';
import { DEFAULT_ANALYTICS_SETTINGS } from '@wooconnect/shared';
import { StatCard } from '@/components/ui/StatCard';
import { Card } from '@/components/ui/Card';
import { OrderStatusBadge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonTable, SkeletonChart, SkeletonCard } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { formatCurrency, formatRelativeTime, formatDate, formatNumber } from '@/utils/format';
import { cn } from '@/utils/cn';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSiteId = useUIStore((s) => s.activeSiteId);
  const { data: dashboard, isLoading, isError, refetch, manualRefresh, isRefreshing } = useDashboard(activeSiteId);
  const activeSite = useUIStore((s) => s.activeSite);

  const analyticsSettings = useAnalyticsSettings(activeSiteId);
  const settings = analyticsSettings.data ?? DEFAULT_ANALYTICS_SETTINGS;
  const dashDateRange = useMemo(() => computeDateRange(settings.defaultPreset, settings.weekStartsOn, settings.timezone || undefined), [settings.defaultPreset, settings.weekStartsOn, settings.timezone]);
  const dashDateParams = useMemo(() => ({
    after: dashDateRange.after,
    before: dashDateRange.before,
    interval: dashDateRange.interval,
    compare: 'previousPeriod',
  }), [dashDateRange]);
  const { data: revenueReport, isLoading: revenueLoading, isError: revenueError } = useRevenueReport(activeSiteId, dashDateParams);

  if (!activeSiteId) {
    return <EmptyState icon={ShoppingBagIcon} title={t('dashboard.selectStore')} description={t('sites.noSites')} />;
  }

  if (isError) {
    return <ErrorState onRetry={() => refetch()} />;
  }

  const stats = dashboard?.stats;
  const currency = stats?.currencySymbol ?? '\u20AC';
  // Dashboard revenue = net_revenue from WC Analytics (always excl. tax)
  const vatCaption = stats?.taxDisplayShop === 'incl' ? t('analytics.exclVat') : undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.title')}</h1>
        <Button variant="ghost" size="sm" onClick={() => manualRefresh()} loading={isRefreshing} leftIcon={<ArrowPathIcon className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />}>
          {t('common.refresh')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.ordersToday')}
          value={stats ? String(stats.ordersToday) : '0'}
          icon={ShoppingBagIcon}
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.revenueToday')}
          value={stats ? formatCurrency(stats.revenueToday) : formatCurrency(0)}
          icon={CurrencyEuroIcon}
          subtitle={vatCaption}
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.revenueWeek')}
          value={stats ? formatCurrency(stats.revenueThisWeek) : formatCurrency(0)}
          icon={CurrencyEuroIcon}
          subtitle={vatCaption}
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.revenueMonth')}
          value={stats ? formatCurrency(stats.revenueThisMonth) : formatCurrency(0)}
          icon={CurrencyEuroIcon}
          subtitle={vatCaption}
          isLoading={isLoading}
        />
      </div>

      {/* Store Health */}
      {activeSite && (
        <Card>
          <div className="flex items-center gap-3">
            <SignalIcon className="h-5 w-5 text-gray-400" />
            <div className="flex items-center gap-2">
              <span className={cn('h-2.5 w-2.5 rounded-full', activeSite.status === 'active' ? 'bg-green-500' : activeSite.status === 'error' ? 'bg-red-500' : 'bg-yellow-500')} />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{activeSite.name}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{activeSite.status === 'active' ? t('sites.online') : t('sites.offline')}</span>
            </div>
            {activeSite.wcVersion && <span className="ml-auto text-xs text-gray-400">WC {activeSite.wcVersion}</span>}
          </div>
        </Card>
      )}

      {/* Revenue Chart */}
      {revenueLoading ? (
        <SkeletonChart />
      ) : revenueError ? (
        <Card title={t('analytics.netRevenue')}>
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('common.error')}</p>
        </Card>
      ) : revenueReport && revenueReport.intervals.length > 0 ? (
        <Card title={`${t('analytics.netRevenue')} - ${t(`analytics.${settings.defaultPreset}`)}`}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueReport.intervals}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#af7964" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#af7964" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => formatDate(d, dashDateRange.interval === 'month' ? 'MMM' : 'dd/MM')}
                  className="text-xs"
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(v: number) => `${currency}${v}`}
                  className="text-xs"
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  width={70}
                />
                <Tooltip
                  formatter={(v) => [formatCurrency(v as number), t('analytics.netRevenue')]}
                  labelFormatter={(l) => formatDate(String(l), 'dd MMM yyyy')}
                  contentStyle={{
                    background: 'var(--tooltip-bg, #fff)',
                    color: 'var(--tooltip-text, #374151)',
                    border: '1px solid var(--tooltip-border, #e5e7eb)',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                />
                <Area type="monotone" dataKey="netRevenue" stroke="#af7964" fill="url(#revenueGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ) : null}

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.totalProducts')}
          value={stats ? String(stats.totalProducts) : '0'}
          icon={CubeIcon}
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.totalCustomers')}
          value={stats ? String(stats.totalCustomers) : '0'}
          icon={UsersIcon}
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.pending')}
          value={stats ? String(stats.pendingOrders) : '0'}
          icon={ClockIcon}
          isLoading={isLoading}
        />
        <StatCard
          title={t('dashboard.processing')}
          value={stats ? String(stats.processingOrders) : '0'}
          icon={ShoppingBagIcon}
          isLoading={isLoading}
        />
      </div>

      {/* Low Stock Alert */}
      {stats && (stats.lowStockCount > 0 || stats.outOfStockCount > 0) && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('dashboard.lowStockAlert')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('dashboard.lowStockDesc', { low: stats.lowStockCount, out: stats.outOfStockCount })}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/products?stock=low')}>
              {t('analytics.viewProducts')}
            </Button>
          </div>
        </Card>
      )}

      {/* Recent Orders */}
      <Card
        title={t('dashboard.recentOrders')}
        action={
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
            {t('common.viewAll')} {'\u2192'}
          </Button>
        }
      >
        {isLoading ? (
          <SkeletonTable rows={5} cols={4} />
        ) : dashboard?.recentOrders && dashboard.recentOrders.length > 0 ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">#</th>
                  <th className="px-5 py-2">{t('orders.customer')}</th>
                  <th className="px-5 py-2">{t('orders.total')}</th>
                  <th className="px-5 py-2">{t('orders.status')}</th>
                  <th className="px-5 py-2">{t('orders.date')}</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recentOrders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">
                      #{order.number}
                    </td>
                    <td className="px-5 py-3 text-gray-600 dark:text-gray-300">{order.customerName}</td>
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(order.total, order.currency)}
                    </td>
                    <td className="px-5 py-3"><OrderStatusBadge status={order.status} /></td>
                    <td className="px-5 py-3 text-gray-500 dark:text-gray-400" title={formatDate(order.dateCreated, 'dd/MM/yyyy HH:mm')}>
                      {formatRelativeTime(order.dateCreated)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={ShoppingBagIcon} title={t('orders.noOrders')} />
        )}
      </Card>
    </div>
  );
}
